/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import {
  EuiPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiFieldText,
  EuiBasicTable,
  EuiBasicTableColumn,
  EuiSpacer,
} from "@inscopix/ideas-eui";
import { ToolVersionSelectorBase } from "pages/gdt/cell-editors/ToolVersionEditorBase";
import { useWorkflowEditorContext } from "components/WorkflowEditor/WorkflowEditorProvider/WorkflowEditorProvider";
import { WorkflowEditorAddTablePopover } from "./WorkflowEditorAddTablePopover";
import { WorkflowEditorPageGraph } from "../Graph/WorkflowEditorPageGraph";
import { ButtonGroupTableGraph } from "components/WorkflowEditor/components/ButtonGroupTableGraph";

export interface ToolVersion {
  id: number;
  version: string;
}

export interface TableListItem {
  id: string;
  /** non-editable table name */
  stepName: string;
  toolName: string;
  tableVersion: { id: number; version: string };
  defaultVersion: { id: number };
}

export interface PanelTableInfoProps {
  tables: TableListItem[];
}

export const PanelTableInfo: React.FC<PanelTableInfoProps> = ({ tables }) => {
  const setTableInfo = useWorkflowEditorContext((s) => s.setTableOverride);

  const [view, setView] = useState<"TABLE" | "GRAPH">("TABLE");

  const columns: Array<EuiBasicTableColumn<TableListItem>> = [
    {
      field: "stepName",
      name: "Workflow Step",
      render: (_, table) => (
        <EuiFieldText
          key={`workflow step ${table.id}`}
          compressed
          placeholder="Workflow Step"
          value={table.stepName}
          onChange={(e) =>
            setTableInfo(table.id, (prev) => {
              prev.name = e.target.value;
              return prev;
            })
          }
          aria-label={`Workflow step ${table.stepName}`}
        />
      ),
    },
    {
      field: "toolName",
      name: "Tool Name",
      render: (_, table) => (
        <EuiText size="s" key={`tool name ${table.toolName}`}>
          <strong>{table.toolName}</strong>
        </EuiText>
      ),
    },
    {
      field: "tableVersion",
      name: "Version",
      render: (_, table) => (
        <ToolVersionSelectorBase
          key={`tool version selector ${table.id}`}
          defaultVersion={table.defaultVersion}
          selectedToolVersion={table.tableVersion}
          onSelectVersion={(version) => {
            setTableInfo(table.id, (prev) => {
              prev.toolVersion = version;
              return prev;
            });
          }}
        />
      ),
    },
  ];

  return (
    <>
      <EuiFlexGroup
        css={{
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <EuiFlexItem>
          <ButtonGroupTableGraph selected={view} onChange={setView} />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <WorkflowEditorAddTablePopover />
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer />
      {view === "GRAPH" ? (
        <EuiPanel paddingSize="m" style={{ height: "100%" }}>
          <WorkflowEditorPageGraph />
        </EuiPanel>
      ) : (
        <EuiPanel paddingSize="m">
          <EuiBasicTable
            items={tables}
            columns={columns}
            itemId="rowId"
            noItemsMessage="No tables added yet."
          />
        </EuiPanel>
      )}
    </>
  );
};
